import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Landmark } from "lucide-react";

export default function Footer() {
  const y = new Date().getFullYear();
  return (
    <footer className="mt-24 navy-bg text-white" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-6 gap-10">
        <div className="col-span-2">
          <div className="brand-mark brand-mark-inverted text-2xl mb-1 leading-none">
            <span className="word">VENTURAZ</span>
          </div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-gold/80 mb-4" data-testid="footer-byline">
            Powered by CorpZo
          </div>
          <p className="text-white/70 text-sm max-w-md leading-relaxed">
            A Private Market Intelligence platform. VenturaZ is not a stock exchange,
            public securities marketplace, alternative investment fund, or crowdfunding
            platform. Access is permission-based and controlled.
          </p>
          {/* Sister-vertical mark — VenturaZ Money. Copper-tinted to match
              the debt brand palette; the Landmark glyph is the same icon
              used across the money admin UI. */}
          <Link
            to="/money"
            className="mt-6 inline-flex items-center gap-3 group"
            data-testid="footer-money-brand"
            title="VenturaZ Money — India's honest loan advisor"
          >
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#B8860B]/60 group-hover:border-[#FFD98A] transition-colors"
              style={{ background: "linear-gradient(135deg, rgba(122,78,29,0.55) 0%, rgba(184,134,11,0.35) 100%)" }}
              aria-hidden="true"
            >
              <Landmark size={16} className="text-[#FFD98A]" />
            </span>
            <span className="leading-tight">
              <span className="brand-mark brand-mark-inverted text-[18px] leading-none">
                <span className="word">VENTURAZ</span>
                <span className="super" style={{ color: "#F5C97A" }}>MONEY</span>
              </span>
              <span className="block text-[10px] font-mono uppercase tracking-[0.28em] text-[#F5C97A]/85 mt-1">
                Debt Ecosystem · Loan Advisor
              </span>
            </span>
          </Link>
          <div className="mt-6 flex flex-col gap-1 text-[12px] text-white/60">
            <div className="flex items-center gap-2"><Mail size={12} className="text-gold" /> hello@venturaz.com</div>
            <div className="flex items-center gap-2"><MapPin size={12} className="text-gold" /> Noida, Uttar Pradesh, India</div>
          </div>
        </div>
        <div>
          <div className="overline mb-4">Platform</div>
          <ul className="space-y-2 text-sm text-white/75 font-heading">
            <li><Link to="/for-startups" className="hover:text-[#FFD700] transition-colors" data-testid="footer-for-startups">For Startups</Link></li>
            <li><Link to="/for-investors" className="hover:text-[#FFD700] transition-colors" data-testid="footer-for-investors">For Investors</Link></li>
            <li><Link to="/money" className="hover:text-[#F5C97A] transition-colors inline-flex items-center gap-1" data-testid="footer-money-link"><Landmark size={11} className="text-[#F5C97A]" /> VenturaZ Money</Link></li>
            <li><Link to="/marketplace" className="hover:text-[#FFD700] transition-colors" data-testid="footer-marketplace">Verified Directory</Link></li>
            <li><Link to="/how-it-works" className="hover:text-[#FFD700] transition-colors" data-testid="footer-how-it-works">How It Works</Link></li>
            <li><Link to="/pricing" className="hover:text-[#FFD700] transition-colors" data-testid="footer-pricing">Pricing</Link></li>
          </ul>
        </div>
        {/* Dedicated VenturaZ Money column — surfaces the loan verticals so
            borrowers landing on any equity page can navigate the debt
            offering without hunting through the top-nav. Uses copper
            accent instead of gold to visually separate the debt vertical. */}
        <div>
          <div className="overline mb-4" style={{ color: "#F5C97A" }} data-testid="footer-money-col-heading">Money</div>
          <ul className="space-y-2 text-sm text-white/75 font-heading">
            <li><Link to="/money" className="hover:text-[#F5C97A] transition-colors" data-testid="footer-money-home">Loan Advisor</Link></li>
            <li><Link to="/venturaz-money/products" className="hover:text-[#F5C97A] transition-colors" data-testid="footer-money-products">Products</Link></li>
            <li><Link to="/venturaz-money/banks" className="hover:text-[#F5C97A] transition-colors" data-testid="footer-money-banks">85+ Lenders</Link></li>
            <li><Link to="/venturaz-money/become-partner" className="hover:text-[#F5C97A] transition-colors" data-testid="footer-money-partner">Become a Partner</Link></li>
            <li><Link to="/money#emi" className="hover:text-[#F5C97A] transition-colors" data-testid="footer-money-emi">EMI Calculator</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline mb-4">Company</div>
          <ul className="space-y-2 text-sm text-white/75 font-heading">
            <li><Link to="/cms/about" className="hover:text-[#FFD700] transition-colors" data-testid="footer-about">About</Link></li>
            <li><Link to="/cms/contact" className="hover:text-[#FFD700] transition-colors" data-testid="footer-contact">Contact</Link></li>
            <li><Link to="/resources" className="hover:text-[#FFD700] transition-colors" data-testid="footer-insights">Insights</Link></li>
            <li><Link to="/support" className="hover:text-[#FFD700] transition-colors" data-testid="footer-support">Support</Link></li>
            <li><Link to="/admin/login" className="hover:text-[#FFD700] transition-colors" data-testid="footer-admin">Admin Access</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline mb-4">Legal & Compliance</div>
          <ul className="space-y-2 text-sm text-white/75 font-heading">
            <li><Link to="/cms/terms" className="hover:text-[#FFD700] transition-colors" data-testid="footer-terms">Terms of Use</Link></li>
            <li><Link to="/cms/privacy" className="hover:text-[#FFD700] transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/cms/cookie-policy" className="hover:text-[#FFD700] transition-colors" data-testid="footer-cookies">Cookie Policy</Link></li>
            <li><Link to="/cms/refund-policy" className="hover:text-[#FFD700] transition-colors" data-testid="footer-refund">Refund Policy</Link></li>
            <li><Link to="/cms/compliance-declarations" className="hover:text-[#FFD700] transition-colors" data-testid="footer-compliance">Declarations</Link></li>
            <li><Link to="/cms/risk-disclosure" className="hover:text-[#FFD700] transition-colors" data-testid="footer-risk">Risk Disclosure</Link></li>
            <li><Link to="/cms/investor-disclaimer" className="hover:text-[#FFD700] transition-colors" data-testid="footer-investor-disclaimer">Investor Disclaimer</Link></li>
            <li><Link to="/cms/startup-disclaimer" className="hover:text-[#FFD700] transition-colors" data-testid="footer-startup-disclaimer">Startup Disclaimer</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 bg-black/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 text-[11px] text-white/50 leading-relaxed">
          <b className="text-white/70">Regulatory Notice.</b> VenturaZ is a technology platform for Private Market Intelligence,
          diligence workflows and capital-access enablement. Nothing on VenturaZ constitutes investment advice, a
          solicitation, a public offer, or a guarantee of return. Startups on VenturaZ are not guaranteed introductions,
          meetings, term sheets or funding. Investors must conduct independent due diligence and consult qualified
          professionals before committing capital. VenturaZ complies with the Digital Personal Data Protection Act, 2023.
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-white/50">
          <div data-testid="footer-legal-line">© {y} VenturaZ · Ultiwise Ventures Private Limited, Noida · Powered by CorpZo. All rights reserved.</div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/cms/terms" className="hover:text-gold">Terms</Link>
            <Link to="/cms/privacy" className="hover:text-gold">Privacy</Link>
            <Link to="/cms/cookie-policy" className="hover:text-gold">Cookies</Link>
            <Link to="/cms/compliance-declarations" className="hover:text-gold">Compliance</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
